<script lang="ts">
  import type { RunnerMessage } from '../selftest/runner.worker';
  import { getCapabilityReport, reportToText } from '../platform/capabilities';
  import CapabilityReport from './CapabilityReport.svelte';
  import { copyText } from './clipboard';

  interface Row {
    group: string;
    name: string;
    status: 'pending' | 'pass' | 'fail';
    detail: string;
    ms: number;
  }

  let rows = $state<Row[]>([]);
  let running = $state(false);
  let totalMs = $state(0);
  let copied = $state<'idle' | 'ok' | 'fail'>('idle');
  let worker: Worker | null = null;

  const passed = $derived(rows.filter((r) => r.status === 'pass').length);
  const failed = $derived(rows.filter((r) => r.status === 'fail').length);

  function run() {
    worker?.terminate();
    running = true;
    rows = [];
    worker = new Worker(new URL('../selftest/runner.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<RunnerMessage>) => {
      const m = e.data;
      if (m.type === 'list') {
        rows = m.tests.map((t) => ({ ...t, status: 'pending', detail: '', ms: 0 }));
      } else if (m.type === 'result') {
        const r = rows[m.index];
        if (r) {
          r.status = m.pass ? 'pass' : 'fail';
          r.detail = m.detail;
          r.ms = m.ms;
        }
      } else {
        totalMs = m.totalMs;
        running = false;
        worker?.terminate();
        worker = null;
      }
    };
    worker.onerror = (e) => {
      running = false;
      rows.push({ group: 'Runner', name: 'test worker', status: 'fail', detail: e.message, ms: 0 });
    };
    worker.postMessage('run');
  }

  $effect(() => {
    run();
    return () => worker?.terminate();
  });

  async function copy() {
    const lines = [
      `Self-test: ${passed}/${rows.length} passed, ${failed} failed (${totalMs.toFixed(0)} ms)`,
      ...rows.map(
        (r) =>
          `${r.status === 'pass' ? '✓' : r.status === 'fail' ? '✗' : '…'} [${r.group}] ${r.name} — ${r.detail}`,
      ),
      '',
      reportToText(await getCapabilityReport()),
    ];
    copied = (await copyText(lines.join('\n'))) ? 'ok' : 'fail';
    setTimeout(() => (copied = 'idle'), 2500);
  }
</script>

<section class="panel" data-testid="view-selftest">
  <div class="head">
    <h2>Self-test</h2>
    <div class="actions">
      <button onclick={run} disabled={running}>Run again</button>
      <button onclick={copy} disabled={running} data-testid="copy-selftest">
        {copied === 'ok' ? 'Copied ✓' : copied === 'fail' ? 'Copy failed' : 'Copy report'}
      </button>
    </div>
  </div>
  <p class="hint">
    Checks SoundLab's signal processing (FFT, windows, filters, file formats…) inside this browser.
    If anything fails, click <em>Copy report</em> and send it to your instructor.
  </p>
  <p
    class="summary"
    data-testid="selftest-summary"
    data-state={running ? 'running' : failed ? 'fail' : 'pass'}
  >
    {#if running}
      Running… {passed + failed} / {rows.length}
    {:else if failed}
      ✗ {failed} of {rows.length} tests failed
    {:else}
      ✓ All {rows.length} tests passed ({(totalMs / 1000).toFixed(1)} s)
    {/if}
  </p>
  <table>
    <thead>
      <tr><th></th><th>Area</th><th>Test</th><th>Result</th><th class="ms">ms</th></tr>
    </thead>
    <tbody>
      {#each rows as r (r.group + r.name)}
        <tr data-status={r.status}>
          <td class="mark">{r.status === 'pass' ? '✓' : r.status === 'fail' ? '✗' : '…'}</td>
          <td class="group">{r.group}</td>
          <td>{r.name}</td>
          <td class="detail">{r.detail}</td>
          <td class="ms">{r.status === 'pending' ? '' : r.ms.toFixed(0)}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>
<CapabilityReport />

<style>
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
  h2 {
    margin: 0;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
  }
  .hint {
    color: var(--muted);
    font-size: 0.9rem;
  }
  .summary {
    font-weight: 600;
  }
  .summary[data-state='pass'] {
    color: var(--ok);
  }
  .summary[data-state='fail'] {
    color: var(--bad);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;
  }
  th {
    text-align: left;
    color: var(--muted);
    font-weight: 500;
    border-bottom: 1px solid var(--border);
    padding: 0.2rem 0.4rem;
  }
  td {
    padding: 0.25rem 0.4rem;
    border-bottom: 1px solid var(--border);
    vertical-align: top;
  }
  .mark {
    font-weight: 700;
    width: 1.5rem;
    text-align: center;
  }
  tr[data-status='pass'] .mark {
    color: var(--ok);
  }
  tr[data-status='fail'] .mark,
  tr[data-status='fail'] .detail {
    color: var(--bad);
  }
  .group {
    white-space: nowrap;
    color: var(--muted);
  }
  .detail {
    font-family: var(--mono);
    font-size: 0.78rem;
    color: var(--muted);
    word-break: break-word;
  }
  .ms {
    text-align: right;
    white-space: nowrap;
  }
</style>
